/**
 * The `?ask=<prompt>` deep link's input handling. Pure.
 *
 * A link can carry anything, and whatever it carries lands in the Ask
 * composer with the viewer's name on it. So the text is cleaned to what a
 * person could have typed, capped, and then only PREFILLED: the viewer reads
 * it in the composer and sends it themselves (`AskDeepLinkService`). Nothing
 * from a URL is ever sent on its own.
 */

/** Longest prompt a link may prefill, in characters (code points). */
export const ASK_PROMPT_MAX = 500;

export interface SanitisedAskPrompt {
  readonly text: string;
  /** True when the cap cut it. */
  readonly truncated: boolean;
}

// Characters a person cannot type into the composer but a link can smuggle in:
// C0/C1 controls (newlines are folded to spaces first), zero-width joiners and
// spaces, the byte-order mark, and the bidi overrides and isolates that make
// text display in an order other than the one it will be sent in.
const INVISIBLE_RANGES: readonly (readonly [number, number])[] = [
  [0x0000, 0x001f], [0x007f, 0x009f], // C0 / C1 controls
  [0x200b, 0x200f], // zero-width space, joiners, LRM / RLM
  [0x2028, 0x202e], // line / paragraph separators, bidi embeddings + overrides
  [0x2060, 0x2069], // word joiner, invisible operators, bidi isolates
  [0xfeff, 0xfeff], // byte-order mark
];
// Built from code points rather than written as a literal so no tool or
// editor can turn an escape into the invisible character it names.
const INVISIBLE = new RegExp(
  '[' + INVISIBLE_RANGES.map(([a, b]) => hex(a) + '-' + hex(b)).join('') + ']',
  'g',
);
function hex(cp: number): string {
  return '\\u' + cp.toString(16).padStart(4, '0');
}
const MARKUP = /<[^>]*>/g;

/**
 * Clean a prompt from a link. Returns null when nothing usable is left, so
 * the caller has one test for "do nothing".
 *
 * Accepts what a router hands over (`string | string[] | undefined`): the
 * first string of a repeated parameter wins.
 */
export function sanitiseAskPrompt(raw: unknown, max = ASK_PROMPT_MAX): SanitisedAskPrompt | null {
  const value = Array.isArray(raw) ? raw.find(v => typeof v === 'string') : raw;
  if (typeof value !== 'string') return null;
  const cleaned = value
    .normalize('NFC')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(INVISIBLE, '')
    .replace(MARKUP, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;
  // By code point, so a cap never splits an emoji or a surrogate pair.
  const points = Array.from(cleaned);
  if (points.length <= max) return { text: cleaned, truncated: false };
  return { text: points.slice(0, max).join('').trimEnd(), truncated: true };
}

/**
 * The raw `ask` parameter from a router URL (`/home?ask=…`, hash routing has
 * already moved it after the `#`) or from a page search string (`?ask=…`
 * before the `#`, which is how some launchers write it). Null when absent.
 * `URLSearchParams` decodes once; nothing is decoded twice.
 */
export function askParamFrom(url: string | null | undefined): string | null {
  if (!url) return null;
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1).split('#')[0] : '';
  if (!query) return null;
  // URLSearchParams never throws: a malformed escape is kept literally.
  return new URLSearchParams(query).get('ask');
}
